import HistoryLogTable from "@/components/History/HistoryLogTable";
import {
  SaveAndCancel,
  StickyActionsHeader,
} from "@/components/StickyActionsHeader";
import SpinLoader from "@/containers/Layout/MainLayout/SpinLoader";
import { CONTENT_WIDTH, GUTTER_SIZE, RIGHTS } from "@/enums";
import { routes } from "@/helpers";
import { matchType } from "@/propTypes";
import { useUserContext } from "@/providers";
import {
  useGetAdGroupsQuery,
  useGetHistoryLogsByIdQuery,
  useGetPermissionQuery,
  useSaveAssignmentsMutation,
  useSavePermissionMutation,
  useSaveRightsMutation,
} from "@/services";
import { faUnlockKeyhole } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Card, Col, Row, Space } from "antd";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AddExcludeUsers, DetailsCard } from "../Layout";
import { PageWrapper } from "../PageWrapper";
import { PermissionRights } from "./components/PermissionRights";
import { getIsFormDirty, trimUserAssignments } from "./helper/getIsFormDirty";
import { validatePermission } from "./helper/validatePermission";
import { useMemo } from "react";

const PermissionEditor = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { id: permissionId } = useParams();
  const { hasRight } = useUserContext();


  const [permission, setPermission] = useState();
  
  const validationMessages = useMemo(
    () => (permission ? validatePermission(permission) : []),
    [permission]
  );
  const [pageActions, setPageActions] = useState();

  const { isFetching: isLoading, data: permissionData, refetch: refetchPermissionsData } = useGetPermissionQuery(
    permissionId,
    { skip: !permissionId }
  );
  const [savePermission, { isLoading: isSavingPermission }] =
    useSavePermissionMutation();
  const [saveRights, { isLoading: isSavingRights }] = useSaveRightsMutation();
  const [saveAssignments, { isLoading: isSavingAssignments }] =
    useSaveAssignmentsMutation();
  const isSaving = isSavingPermission || isSavingRights || isSavingAssignments;
  const { data: adGroups, isFetching: isAdGroupLoading } = useGetAdGroupsQuery(
    permissionId,
    { skip: !permissionId }
  );
  const { refetch: refetchHistory } = useGetHistoryLogsByIdQuery(
    { parentEntityId: permissionId },
    { skip: !permissionId }
  );

  useEffect(() => {
    if (!permissionData) return;

    setPermission(permissionData);
  }, [permissionData]);

  useEffect(() => {
    if (isLoading) {
      setPageActions();
      return;
    }

    const isRightsDirty = !_.isEqual(
      [...(permission?.rights || [])].sort(),
      [...(permissionData?.rights || [])].sort()
    );

    const isAdGroupsDirty = !_.isEqual(
      [...(permission?.adGroupAssignments || [])].sort(),
      [...(permissionData?.adGroupAssignments || [])].sort()
    );

    const isUserAssignmentsDirty = !_.isEqual(
      trimUserAssignments(permission?.userAssignments),
      trimUserAssignments(permissionData?.userAssignments)
    );

    const isCoreDirty =
      !permissionId ||
      permission?.name !== permissionData?.name ||
      permission?.description !== permissionData?.description;

    const showSuccessToasts = () => {
      if (permissionId) {
        refetchHistory();
        refetchPermissionsData();
      }
      toast.success(
        intl.formatMessage({ id: "general.toastChangesSaved" })
      );
      toast.info(
        intl.formatMessage({ id: "general.searchIndexInfoMessage" })
      );
    };

    const showErrorToast = () => {
      toast.error(
        intl.formatMessage({ id: "general.toastSomethingWentWrong" })
      );
    };

    const handleSaveButtonClick = () => {
      const { id, name, description, rights, userAssignments, adGroupAssignments } =
        permission;
      const corePermission = { id, name, description };
      const trimmedUserAssignments = trimUserAssignments(userAssignments);
      const isNewPermission = !permissionId;

      if (isNewPermission) {
        savePermission({ permission: corePermission })
          .unwrap()
          .then((createdPermission) => {
            const newRights = rights || [];
            const hasAdGroups = !!adGroupAssignments?.length;
            const hasUserAssignments = !!trimmedUserAssignments.length;

            const afterAssignmentsSaved = () =>
              navigate(routes.admin.permissions.get(createdPermission.id));

            const rightsPromise = newRights.length
              ? saveRights({
                  permissionId: createdPermission.id,
                  rights: newRights,
                }).unwrap()
              : Promise.resolve();

            const assignmentsPromise =
              hasAdGroups || hasUserAssignments
                ? saveAssignments({
                    permissionId: createdPermission.id,
                    adGroupIds: hasAdGroups ? adGroupAssignments : undefined,
                    userAssignments: hasUserAssignments
                      ? trimmedUserAssignments
                      : undefined,
                  }).unwrap()
                : Promise.resolve();

            return Promise.all([rightsPromise, assignmentsPromise]).then(
              afterAssignmentsSaved
            );
          })
          .then(showSuccessToasts)
          .catch(showErrorToast);
        return;
      }

      const calls = [];
      if (isCoreDirty) {
        calls.push(
          savePermission({ permission: corePermission }).unwrap()
        );
      }
      if (isRightsDirty) {
        calls.push(
          saveRights({ permissionId, rights: rights || [] }).unwrap()
        );
      }
      if (isAdGroupsDirty || isUserAssignmentsDirty) {
        calls.push(
          saveAssignments({
            permissionId,
            adGroupIds: isAdGroupsDirty ? adGroupAssignments || [] : undefined,
            userAssignments: isUserAssignmentsDirty
              ? trimmedUserAssignments
              : undefined,
          }).unwrap()
        );
      }

      if (!calls.length) return;

      Promise.all(calls).then(showSuccessToasts).catch(showErrorToast);
    };

    const isFormDirty = getIsFormDirty({
      permission,
      permissionData,
      permissionId,
    });

    setPageActions(
      <StickyActionsHeader
        rightChildren={
          <SaveAndCancel
            pageName="permission-editor"
            saveOnClick={handleSaveButtonClick}
            cancelOnClick={() => navigate(routes.admin.permissions.list)}
            saveDisabled={!!validationMessages.length || (permissionData && !isFormDirty)}
            loading={isSaving}
          />
        }
      />
    );
  }, [
    intl,
    isLoading,
    isSaving,
    navigate,
    permission,
    permissionData,
    permissionId,
    savePermission,
    saveRights,
    saveAssignments,
    validationMessages.length,
    refetchHistory,
  ]);

  const updatePermission = (updates) => {
    setPermission({
      ...permission,
      ...updates,
    });
  };

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "permissions.title" })}
      backButtonRoute={routes.admin.permissions.list}
      pageActions={pageActions}
    >
      {permissionId && (isLoading || isAdGroupLoading || !permission) ? (
        <SpinLoader />
      ) : (
        <Row gutter={GUTTER_SIZE.large}>
          <Col span={CONTENT_WIDTH.half}>
            <div className="pb-2">
              <DetailsCard
                setModel={updatePermission}
                name={permission?.name}
                description={permission?.description}
                loading={isLoading}
              />
            </div>
            <AddExcludeUsers
              adGroups={adGroups}
              adGroupAssignments={permission?.adGroupAssignments}
              userAssignments={permission?.userAssignments}
              usersLoading={isLoading}
              usersExcludedLoading={isLoading}
              adGroupsLoading={isAdGroupLoading}
              setModel={updatePermission}
              entityType="permissions"
            />
          </Col>
          <Col span={CONTENT_WIDTH.half}>
            <Card
              title={
                <Space>
                  <FontAwesomeIcon icon={faUnlockKeyhole} />
                  {intl.formatMessage({ id: "permissions.rightsCardTitle" })}
                </Space>
              }
              style={{ height: "auto", marginBottom: "12px" }}
              loading={isLoading}
            >
              <PermissionRights
                selectedRights={permission?.rights}
                canEditRights={hasRight([RIGHTS.writePermissions])}
                setSelectedRights={(rights) =>
                  updatePermission({ rights: rights || [] })
                }
              />
            </Card>
            {validationMessages.map((validationMessage) => (
              <Alert
                key={validationMessage}
                message={validationMessage}
                type="error"
                showIcon
              />
            ))}
            <Card loading={isLoading}>
              <HistoryLogTable parentEntityId={permissionId} />
            </Card>
          </Col>
        </Row>
      )}
    </PageWrapper>
  );
};

PermissionEditor.propTypes = {
  match: matchType,
};

export default PermissionEditor;
